import type { State } from '../State';
import type { PublicProfile } from '../profile/profile.state';
import {
  PetAttribute,
  PetEvolutionStage,
  PetSpeciesID,
  PetState,
} from '../pet';
import * as Pet from '../pet';
import * as Fate from '../fate';
import * as Decoration from '../decoration';
import { getLocale } from '../getters/user';
import createAsyncGetters, {
  AsyncGetterAPI,
} from '../config/asyncGetters/createAsyncGetters';
import { duration } from '../utils/duration';
import { SelectedItemState } from '../decoration/decoration.state';
import { getRandomItemFromArray } from '../utils/random';
import * as Accessory from '../accessory';
import { SelectedAccessoryState } from '../accessory/accessory.schema';
import { getChatbotMessageChannel } from '../chatbot/chatbot.getters';
import features from '../ruleset/features';
import { getBuruFriends, isBuruFriend } from '../friends/friends.getters';
import { Answers } from '../conversation/conversation.schema';
import {
  getNextPetState,
  getStatusEffectStartTime,
} from '../fate/fate.getters';
import type { NextPetState } from '../fate/fate.getters';
import { FatePermanentStatusEffect } from '../fate/fate.ruleset';
import { getVisitedCount, getVisitsCount } from './visit.getters';

import { GENERIC_AVATAR } from '../profile/assets';
import { getPlayerProfileAvatarFrame } from '../profile/profile.getters';
import { AvatarFrameId } from '../profile/frames';
import { visitListWithNonFriendsLimit } from './visit.ruleset';
import { availableToVisitWhere, isOnlineForVisit } from './visit.presence';
import type { Presence } from '@play-co/replicant';

export const visitAsyncGetters = createAsyncGetters({
  /**
   * Get all attribute values of friend
   */
  getFriendAttributeValues: async (playerId: string, api) => {
    const statesMap = await api.fetchStates([playerId]);
    const playerState = statesMap[playerId];

    if (!playerState) {
      return null;
    }

    const now = Math.floor(api.date.now() / 1000) * 1000;

    return Pet.getters.getAttributeValues(playerState.state, now);
  },

  getPetVisitDetails: async (
    friendId: string,
    api,
  ): Promise<FriendVisitDetails | null> => {
    const now = api.date.now();
    // Debug purposes.
    if (
      process.env.IS_DEVELOPMENT &&
      process.env.REPLICANT_OFFLINE &&
      friendId === 'mock'
    ) {
      return getMockVisitDetails(now, api.math.random);
    }

    const [friendEntry, ownState] = await Promise.all([
      api.fetchStates([friendId]).then((states) => states[friendId]),

      api.getOwnState(),
    ]);

    if (!friendEntry?.state) {
      return null;
    }

    const friendState = friendEntry.state;

    if (!friendState.publicProfile && !isBuruFriend(ownState, friendId)) {
      throw Error(`Cannot visit a non friend without a public profile`);
    }

    return getVisitDetails(ownState, friendEntry, now);
  },

  getPetVisitDetailsAndNextFriendPetState: async (
    friendId: string,
    api,
  ): Promise<{
    visitDetails: FriendVisitDetails | null;
    nextFriendPetState: NextPetState | null;
  }> => {
    const now = api.date.now();

    // Debug purposes.
    if (
      process.env.IS_DEVELOPMENT &&
      process.env.REPLICANT_OFFLINE &&
      friendId === 'mock'
    ) {
      return {
        visitDetails: await getMockVisitDetails(now, api.math.random),
        nextFriendPetState: null,
      };
    }

    const [friendEntry, ownState] = await Promise.all([
      api.fetchStates([friendId]).then((states) => states[friendId]),

      api.getOwnState(),
    ]);

    if (!friendEntry?.state) {
      return { visitDetails: null, nextFriendPetState: null };
    }

    const friendState = friendEntry.state;

    if (!friendState.publicProfile && !isBuruFriend(ownState, friendId)) {
      throw Error(`Cannot visit a non friend without a public profile`);
    }

    const visitDetails = getVisitDetails(ownState, friendEntry, now);

    const nextFriendPetState = getNextPetState(friendState, now);

    return { visitDetails, nextFriendPetState };
  },

  /**
   * Will return a list of friends for visiting. Criterial can depend based on AB tests.
   */
  getVisitFriendsList: async (_: void, api) => {
    const state = await api.getOwnState();

    // Get list based on AB test bucket.
    const list: VisitListData = await getSortedVisitListWithNonFriends(
      state,
      api,
    );

    // Convert data to required format.
    const data = Object.values(list).map((player) => {
      const isFriend = player.isFriend;
      const privateName = isFriend ? player.profileName : 'unknown';
      const privatePhoto = isFriend ? player.profilePhoto : GENERIC_AVATAR;

      const name = player.hasPublicProfile
        ? player.publicProfileName
        : privateName;
      const photo = player.hasPublicProfile
        ? player.publicProfilePhoto
        : privatePhoto;

      return {
        id: player.id,
        lastLogin: player.updatedAt,
        name,
        photo,
        frame: player.profileAvatarFrame as AvatarFrameId,
        species: (player.petSpecies as PetSpeciesID) ?? 'dog',
        petState: player.petState,
        isFriend,
        canAcceptFriendRequest: player.canAcceptFriendRequest,
      };
    });

    return data;
  },

  getRecentVisitorList: async (
    limitTo: number,
    api,
  ): Promise<VisitorData[]> => {
    const state = await api.getOwnState();

    const { recentVisitsFrom } = state.visit;
    const visitorIds = Object.keys(recentVisitsFrom);
    const friendIds = getBuruFriends(state);

    // Filter to a 30-day window. Resolved from A/B test 0453 to always-enabled.
    const thirtyDaysAgo = api.date.now() - duration({ days: 30 });
    const ids = visitorIds
      .filter((id) => recentVisitsFrom[id] >= thirtyDaysAgo)
      // Sort by last visit.
      .sort((a, b) => {
        return recentVisitsFrom[b] - recentVisitsFrom[a];
      })
      // Remove excess.
      .slice(0, limitTo);

    // Search top visitors.
    const query = await api.searchPlayers({
      where: {
        id: {
          isOneOf: ids,
        },
      },
      limit: limitTo,
    });

    const data = query.results
      .map((player) => {
        // Player doesn't have a pet.
        if (!player.petSpecies) {
          return null;
        }

        // Player is not a friend and has a private profile.
        if (!player.hasPublicProfile && !friendIds.includes(player.id)) {
          return null;
        }

        // Map to VisitorData.
        return {
          id: player.id,
          lastLogin: player.updatedAt,
          lastVisit: recentVisitsFrom[player.id],
          species: player.petSpecies,
          name: player.hasPublicProfile
            ? player.publicProfileName
            : player.profileName,
          photo: player.hasPublicProfile
            ? player.publicProfilePhoto
            : player.profilePhoto,
          frame: player.profileAvatarFrame as AvatarFrameId,
          isFriend: friendIds.includes(player.id),
          canAcceptFriendRequest: player.canAcceptFriendRequest,
          isOnline: isOnlineForVisit(player.presence),
        };
      })
      // Filter out nulls.
      .filter((x): x is Exclude<typeof x, null> => !!x)
      // Sort by last visit.
      .sort((a, b) => {
        return recentVisitsFrom[b.id] - recentVisitsFrom[a.id];
      });

    return data;
  },

  getCurrentVisitors: async (id: string, api) => {
    const visitors: string[] = [];

    // Fetch visitors in batches of 1000 - we really don't expect to get this many
    // but visiting an online player would create a weird glitchy experience so
    // best to ensure everyone gets kicked out
    const limit = 1000;
    while (true) {
      const { results } = await api.searchPlayers({
        where: {
          id: {
            isNotOneOf: visitors,
          },
          visitingFriendId: {
            isAnyOf: [id],
          },
        },
        fields: [],
        limit,
      });
      visitors.push(...results.map(({ id }) => id));

      if (results.length < limit) {
        break;
      }
    }

    return visitors;
  },
});

export type FriendVisitDetails = PublicProfile & {
  id: string;
  lastLogin: number;
  linePhoto?: string;
  lineName?: string;
  frame: AvatarFrameId;
  isFriend: boolean;
  canAcceptFriendRequest: boolean;
  isOnline: boolean;
  locale: string;
  level: number;
  xp: number;
  visits: number;
  visited: number;
  currentEvolution: {
    species: PetSpeciesID;
    stage: PetEvolutionStage;
    level?: number;
    timestamp: number;
  };
  age: number;
  answers: Answers;
  statusEffects: Record<FatePermanentStatusEffect, boolean>;
  state: PetState;
  deadStateTimestamp?: number;
  notificationSettings: string;
  notificationChannel: 'oa' | 'push' | null;
  attributes: Record<PetAttribute, number> & { name: string; stress: number };
  accessories: SelectedAccessoryState[];
  decorations: SelectedItemState[];
  features: ReturnType<typeof features>;
  revivedPet?: boolean;
};

export type FriendListData = PublicProfile & {
  id: string;
  frame: AvatarFrameId;
  species: PetSpeciesID;
  lastLogin: number;
  isFriend: boolean;
  canAcceptFriendRequest: boolean;
};

export type VisitorData = FriendListData & {
  lastVisit: number;
  isOnline: boolean;
};

/**
 * Get a mock visit user details.
 * @param now
 * @param random
 * @returns
 */
async function getMockVisitDetails(
  now: number,
  random: () => number,
): Promise<FriendVisitDetails> {
  const evolution = getRandomItemFromArray(
    Object.values(Pet.ruleset.evolutions['control']),
    random,
  );
  return {
    id: 'mock',
    isOnline: false,
    name: 'Mock User',
    photo: GENERIC_AVATAR,
    frame: 'default',
    locale: 'en',
    lastLogin: now,
    isFriend: true,
    canAcceptFriendRequest: true,
    visits: 42,
    visited: 41,
    level: 42,
    xp: 42,
    currentEvolution: {
      species: evolution.id,
      stage: evolution.stage,
      level: 1,
      timestamp: now - duration({ days: 1 }),
    },
    age: 42,
    answers: {},
    statusEffects: {
      asleep: false,
      evolving: false,
      sick: false,
      toothDecay: false,
      scared: false,
      dying: false,
      dead: false,
      daycare: false,
      wantToGoOut: false,
      wantToInvite: false,
      hadVisitor: false,
    },
    state: 'happy',
    notificationSettings: 'on',
    notificationChannel: 'oa' as const,
    attributes: {
      name: 'Mock Pet',
      stomach: 100,
      love: 100,
      dirtiness: 0,
      urine: 0,
      toothAcidity: 0,
      stress: 0,
    },
    decorations: [] as SelectedItemState[],
    accessories: [] as SelectedAccessoryState[],
    features: {} as ReturnType<typeof features>,
  };
}

export function getVisitDetails(
  state: State,
  friend: { state: State; presence?: Presence },
  now: number,
): FriendVisitDetails | null {
  const friendState = friend.state;

  const {
    getAttributeValues,
    getCurrentEvolution,
    getPetAge,
    getStressValue,
    getPetName,
  } = Pet.getters;
  const { getAllPermanentStatusEffects, getVisiblePetState } = Fate.getters;
  const currentEvolution = getCurrentEvolution(friendState);
  if (!currentEvolution?.species) {
    return null;
  }

  const { current: level, xp } = friendState.level;
  const { name, photo } = friendState.publicProfile ??
    friendState.profile ?? {
      name: 'Unknown',
      photo: GENERIC_AVATAR,
    };

  // We somehow have a circular dependency on our feature flags which
  // is a huge problem. We badly need to refactor our backend design
  const features = require('../ruleset/features').default;

  // Find out how many times this player has visited us
  const visited = getVisitedCount(state, friendState.id);
  // Find out how many times we have visited this player.
  const visits = getVisitsCount(state, friendState.id);

  const petState = getVisiblePetState(friendState, now, true);

  const arePlayersFriends = isBuruFriend(state, friendState.id);

  return {
    id: friendState.id,
    isOnline: isOnlineForVisit(friend.presence),
    lastLogin: friendState.lastSessionAt,
    isFriend: arePlayersFriends,
    visited,
    visits,
    canAcceptFriendRequest: true,
    name: name ?? 'Unknown',
    photo: photo ?? GENERIC_AVATAR,
    frame: getPlayerProfileAvatarFrame(friendState),
    locale: getLocale(friendState),
    level: level,
    xp,
    currentEvolution,
    age: getPetAge(friendState, now),
    answers: friendState.conversation.answers,
    statusEffects: {
      ...getAllPermanentStatusEffects(friendState, now),
      evolving: false,
    },
    state: petState,
    deadStateTimestamp:
      petState === 'dead'
        ? getStatusEffectStartTime(state, 'dead', now)
        : undefined,
    notificationSettings: friendState.settings.receiveMessages,
    notificationChannel: getChatbotMessageChannel(
      friendState,
      { name: 'visit' },
      now,
    ),
    attributes: {
      name: getPetName(friendState),
      ...getAttributeValues(friendState, now),
      stress: getStressValue(friendState, now),
    },
    accessories: Accessory.getters.getSelectedAccessoryState(friendState),
    // Get all selected decorations form friend state
    // Will be used for setting up FriendsRooms view
    // Use minimal size to reduce sending data
    // On client it should be converted to DecorationItem
    decorations: Decoration.getters.getSelectedDecorationsState(friendState),

    features: features(friendState),

    // Do not share line profile if the players are not friends.
    linePhoto: arePlayersFriends ? friendState.profile?.photo : undefined,
    lineName: arePlayersFriends ? friendState.profile?.name : undefined,
  } satisfies FriendVisitDetails;
}

/**
 * Expected type for visit friends list data.
 */
type VisitListData = {
  id: string;
  updatedAt: number;
  publicProfileName: string;
  publicProfilePhoto: string;
  profileName?: string;
  profilePhoto?: string;
  profileAvatarFrame: string;
  petSpecies: string;
  hasPublicProfile: boolean;
  canAcceptFriendRequest: boolean;
  isFriend: boolean;
  petState?: string;
}[];

/**
 * Visit list getter.
 * - Exclude the players you can’t visit now
 * - Prioritize the players with similar status as you
 * - Friends first, then non-friends with public profiles
 * - Limit to 50 players with a maximum of 10 friends
 */
export async function getSortedVisitListWithNonFriends(
  state: State,
  api: AsyncGetterAPI,
): Promise<VisitListData> {
  const filteredFriends = getBuruFriends(state);
  const searches = [];

  // Query offline friends with pets
  searches.push(friendsSearch(state, api));

  // Query offline non-friends with pets, if user has a public profile
  if (state.publicProfile) {
    searches.push(nonFriendsSearch(state, api));
  }

  // Await all searches
  const responses = await Promise.all(searches);

  // Process results and slice to required limits
  const result: Array<Omit<VisitListData[number], 'isFriend'>> = responses
    .flatMap((res) => res.results)
    .slice(0, visitListWithNonFriendsLimit);

  return result.map((player) => ({
    ...player,
    isFriend: filteredFriends.includes(player.id),
  }));
}

async function friendsSearch(state: State, api: AsyncGetterAPI) {
  const now = api.date.now();
  const filteredFriends = getBuruFriends(state);
  const playerStateScore = Fate.getters.getPetStateScore(state, now);
  const playerStress = Pet.getters.getStressValue(state, now);

  return api.searchPlayers({
    // Read more here: https://www.elastic.co/guide/en/elasticsearch/reference/current/query-dsl-function-score-query.html#function-decay
    scoring: {
      functions: {
        // Try to find friends with similar states
        petStateSorting: {
          // Here is our goal, then friend closer to this value then it will be more hight ranked
          origin: playerStateScore,
          // Default good enough search function
          function: 'gauss',
          // Defines the distance from origin at which the computed score will equal decay parameter
          scale: 5, // We have 11 max score probably 5 is good scale distance from origin
        },
        // Try to find friends with similar stress value
        petStress: {
          // Here is our goal of stress value
          origin: playerStress,
          // Default good enough search function
          function: 'gauss',
          // Defines the distance from origin at which the computed score will equal decay parameter
          scale: 30, // From 0 - 100
        },
        // Sort by last status update message
        offlineUpdateAt: {
          origin: now,
          function: 'gauss',
          scale: duration({ hours: 1 }),
        },
      },
      mode: 'multiply',
    },
    where: {
      // Only players available to visit (offline)
      ...availableToVisitWhere(),
      // Have an active pet
      petSpecies: {
        isNoneOf: [''],
      },
      // Exclude pet in daycare
      petIsInDaycare: {
        isNot: true,
      },
      // Only friends
      id: {
        isOneOf: filteredFriends,
      },
      // Exclude players who haven’t get any status messages for a week
      // They dead for sure
      offlineUpdateAt: {
        greaterThan: api.date.now() - duration({ days: 7 }),
      },
      // Exclude users who are about to debut.
      pendingDebut: {
        is: false,
      },
    },
    fields: [
      'updatedAt',
      'profileName',
      'profilePhoto',
      'hasPublicProfile',
      'publicProfileName',
      'publicProfilePhoto',
      'profileAvatarFrame',
      'petSpecies',
      'petState',
      'canAcceptFriendRequest',
    ],
    // Only 10 friends
    limit: 10,
  });
}

/**
 * Search visitable non-friends, best matches first.
 *
 * Scoring cost grows with the number of matches, so the recent status-update window is searched
 * first, and the full window only when that does not fill the list.
 */
export async function nonFriendsSearch(state: State, api: AsyncGetterAPI) {
  const now = api.date.now();

  // Scoring decays `offlineUpdateAt` on a 1-hour scale, so the best matches are usually recent.
  // Status messages pause overnight, when this falls short of the limit.
  const recent = await searchNonFriends(
    state,
    api,
    now - duration({ hours: 3 }),
  );
  if (recent.results.length >= visitListWithNonFriendsLimit) return recent;

  // Players without a status message for 2 days may be inactive.
  return searchNonFriends(state, api, now - duration({ days: 2 }));
}

function searchNonFriends(
  state: State,
  api: AsyncGetterAPI,
  offlineUpdatedAfter: number,
) {
  const now = api.date.now();
  const playerStateScore = Fate.getters.getPetStateScore(state, now);
  const playerStress = Pet.getters.getStressValue(state, now);

  return api.searchPlayers({
    // Read more here: https://www.elastic.co/guide/en/elasticsearch/reference/current/query-dsl-function-score-query.html#function-decay
    scoring: {
      functions: {
        // Try to find friends with similar states
        petStateSorting: {
          // Here is our goal, then friend closer to this value then it will be more hight ranked
          origin: playerStateScore,
          // Default good enough search function
          function: 'gauss',
          // Defines the distance from origin at which the computed score will equal decay parameter
          scale: 5, // We have 11 max score probably 5 is good scale distance from origin
        },
        // Try to find friends with similar stress value
        petStress: {
          // Here is our goal of stress value
          origin: playerStress,
          // Default good enough search function
          function: 'gauss',
          // Defines the distance from origin at which the computed score will equal decay parameter
          scale: 30, // From 0 - 100
        },
        // Sort by last status update message
        offlineUpdateAt: {
          origin: now,
          function: 'gauss',
          scale: duration({ hours: 1 }),
        },
      },
      mode: 'multiply',
    },
    where: {
      // Only players available to visit (offline)
      ...availableToVisitWhere(),
      petSpecies: {
        isNoneOf: [''],
      },
      // Exclude pet in daycare
      petIsInDaycare: {
        isNot: true,
      },
      id: {
        isNotOneOf: [state.id, ...getBuruFriends(state)],
      },
      hasPublicProfile: {
        is: true,
      },
      // `offlineUpdateAt` is indexed on whole hours, so this bound matches the same players as
      // `greaterThan: offlineUpdatedAfter` while staying stable for an hour, which lets
      // OpenSearch reuse the cached filter.
      offlineUpdateAt: {
        greaterThanOrEqual:
          Math.floor(offlineUpdatedAfter / duration({ hours: 1 })) *
            duration({ hours: 1 }) +
          duration({ hours: 1 }),
      },
      // Exclude users who are about to debut.
      pendingDebut: {
        is: false,
      },
    },
    fields: [
      'updatedAt',
      'hasPublicProfile',
      'publicProfileName',
      'publicProfilePhoto',
      'profileAvatarFrame',
      'petSpecies',
      'petState',
      'canAcceptFriendRequest',
    ],
    limit: visitListWithNonFriendsLimit,
  });
}
